// themeUtil.ts
import { themes, ThemeName, ThemeColors } from './theme.ts';

/**
 * ✅ Basic util:
 * Safely returns the selected theme's colors.
 * If themeName not found, falls back to the light theme.
 */
export const getTheme = (themeName: ThemeName): ThemeColors => {
  return themes[themeName]?.colors || themes.light.colors;
};

/**
 * ✅ Advanced util:
 * Merges selected theme with light theme as fallback.
 * Useful when a theme doesn't define all color keys
 * (prevents undefined values in your UI).
 */
export const getMergedTheme = (themeName: ThemeName): ThemeColors => {
  const base = themes.light.colors;
  const custom = themes[themeName]?.colors || {};
  return { ...base, ...custom };
};

/**
 * 🧩 Optional: Converts a theme to CSS variables and applies to :root
 * (This makes theme usable directly in CSS or Tailwind config)
 */
export const applyTheme = (themeName: ThemeName): void => {
  const theme = getMergedTheme(themeName);

  if (typeof document === 'undefined') return; // for SSR safety

  const root = document.documentElement;

  // Loop through color keys and set CSS vars dynamically
  Object.entries(theme).forEach(([key, value]) => {
    if (typeof value === 'string') {
      root.style.setProperty(`--color-${key}`, value);
    } else if (typeof value === 'object') {
      Object.entries(value).forEach(([subKey, subValue]) => {
        if (typeof subValue === 'string') {
          root.style.setProperty(`--color-${key}-${subKey}`, subValue);
        }
      });
    }
  });
};
